import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, skip, takeUntil } from 'rxjs/operators';

import { MakersFinancesMgrService } from './services/makers-finances-mgr.service';
import { StoreAuxService } from '../services/storeAux/store-aux.service';
import { HasPermissionsService } from '../shared/services/has-permissions/has-permissions.service';
import { FINANCIAL_HUB_SECTION } from './financial-hub.constants';
import { AppState } from '../store/states/app.state';
import { StoreName } from '../store/definitions/store.constants';
import { PurchaseOrderListBanner } from './models/makers-finances-mgr.models';

@Component({
  selector: 'app-financial-hub',
  templateUrl: './financial-hub.component.html',
  styleUrls: ['./financial-hub.component.scss']
})
export class FinancialHubComponent implements OnInit, OnDestroy {
  showBanner = true;
  isListRoute = true;
  isCompaniesRoute = false;
  bannerConfig: PurchaseOrderListBanner | undefined;
  private bannerDismissed = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    /* c8 ignore next */
    private readonly router: Router,
    /* c8 ignore next */
    private readonly financesService: MakersFinancesMgrService,
    /* c8 ignore next */
    private readonly storeAux: StoreAuxService,
    /* c8 ignore next */
    private readonly permissionsService: HasPermissionsService,
    /* c8 ignore next */
    private readonly store: Store<AppState>,
  ) {}

  // Purchase orders tab is visible when the user has any role under the PO section (or is admin).
  get canViewPurchaseOrders(): boolean {
    return this.permissionsService.hasModuleAccess(FINANCIAL_HUB_SECTION.PURCHASE_ORDERS);
  }

  // The PO create CTA follows tab visibility — any PO role (or admin) can reach the create flow.
  get canCreatePO(): boolean {
    return this.canViewPurchaseOrders;
  }

  // Companies tab is visible when the user has any role under the companies section (or is admin).
  get canViewCompanies(): boolean {
    return this.permissionsService.hasModuleAccess(FINANCIAL_HUB_SECTION.COMPANIES);
  }

  // Companies tab shows its own create CTA; the permissions are the same ones that reveal the tab.
  get canCreateCompany(): boolean {
    return this.canViewCompanies;
  }

  ngOnInit(): void {
    this.financesService.getConfigs(this.storeAux.country)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.bannerConfig = res.data.purchase_order_list?.banner;
      });

    this.updateBannerVisibility(this.router.url);

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e: NavigationEnd) => {
        this.updateBannerVisibility(e.url);
      });

    // On country change, leave any PO detail/create/edit view — a PO loaded for one country
    // is invalid under another. The list component handles its own refresh on country change.
    this.store.pipe(
      select(StoreName.country),
      skip(1),
      takeUntil(this.destroy$),
    ).subscribe(() => this.onCountryChange());
  }

  private onCountryChange(): void {
    this.financesService.getConfigs(this.storeAux.country)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.bannerConfig = res.data.purchase_order_list?.banner;
      });

    if (this.isDetailRoute(this.router.url)) {
      // A detail loaded for one country is invalid under another — leave for the matching list.
      const url = this.router.url;
      const target = /\/companies\/\d+/.test(url)
        ? '/financial-hub/companies'
        : '/financial-hub/purchase-orders';
      this.router.navigate([target]);
    }
  }

  private isDetailRoute(url: string): boolean {
    return url.includes('/purchase-orders/create')
      || url.includes('/companies/create')
      || /\/purchase-orders\/\d+/.test(url)
      || /\/companies\/\d+/.test(url);
  }

  private updateBannerVisibility(url: string): void {
    const isDetail = this.isDetailRoute(url);
    this.isListRoute = !isDetail;
    this.isCompaniesRoute = url.includes('/companies');
    this.showBanner = !isDetail && !this.bannerDismissed && this.bannerConfig?.enabled !== false;
  }

  navigateToCreateCompany(): void {
    this.router.navigate(['/financial-hub/companies/create']);
  }

  navigateToCreate(): void {
    this.router.navigate(['/financial-hub/purchase-orders/create']);
  }

  openBannerAction(): void {
    if (this.bannerConfig?.button?.actionUrl) {
      window.open(this.bannerConfig.button.actionUrl, '_blank');
    }
  }

  closeBanner(): void {
    this.bannerDismissed = true;
    this.showBanner = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
